import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, X } from 'lucide-react';
import api from '../../lib/api';
import RichEditor from '../../components/RichEditor';

const EMPTY = { title: '', slug: '', service_area: '', client_type: '', summary: '', challenge: '', approach: '', outcome: '', cover: '', published: true, order: 0 };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120);

export default function CaseStudiesAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/case-studies/admin');
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...edit, slug: edit.slug || slugify(edit.title) };
    try {
      if (edit.id) await api.put(`/case-studies/${edit.id}`, payload);
      else await api.post('/case-studies', payload);
      setEdit(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Delete case study?')) return; await api.delete(`/case-studies/${id}`); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-serif text-cream-50 text-3xl">Case Studies</h1>
          <p className="text-cream-100/60 text-sm mt-1">Showcase real client wins across service areas.</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-4 py-2 text-sm hover:bg-sage-600"><Plus className="h-4 w-4" /> New case study</button>
      </div>

      <div className="bg-forest-900 border border-forest-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-forest-800 text-cream-100/50">
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Title</th>
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Service area</th>
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Order</th>
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-cream-100/50">No case studies yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-forest-800/70">
                <td className="p-3"><div className="text-cream-50">{c.title}</div><div className="text-xs text-cream-100/50 font-mono">/case-studies/{c.slug}</div></td>
                <td className="p-3 text-cream-100/75">{c.service_area}</td>
                <td className="p-3 text-cream-100/60">{c.order}</td>
                <td className="p-3">{c.published ? <span className="text-sage-400 text-xs uppercase tracking-widest">Live</span> : <span className="text-cream-100/45 text-xs uppercase tracking-widest">Draft</span>}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEdit({ ...c })} className="text-sage-400 text-xs hover:underline">Edit</button>
                  <button onClick={() => remove(c.id)} className="text-red-400 text-xs hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div className="bg-forest-900 border border-forest-800 w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b border-forest-800 sticky top-0 bg-forest-900">
              <h2 className="font-serif text-cream-50 text-xl">{edit.id ? 'Edit' : 'New'} case study</h2>
              <button onClick={() => setEdit(null)} className="text-cream-100/60 hover:text-cream-50"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Title</label>
                <input value={edit.title||''} onChange={(e) => setEdit({ ...edit, title: e.target.value, slug: edit.id ? edit.slug : slugify(e.target.value) })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Slug</label>
                  <input value={edit.slug||''} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50 font-mono" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Service area</label>
                  <input value={edit.service_area||''} onChange={(e) => setEdit({ ...edit, service_area: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Client type / label</label>
                  <input value={edit.client_type||''} onChange={(e) => setEdit({ ...edit, client_type: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Display order</label>
                  <input type="number" value={edit.order||0} onChange={(e) => setEdit({ ...edit, order: parseInt(e.target.value||'0') })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Cover image URL</label>
                <input value={edit.cover||''} onChange={(e) => setEdit({ ...edit, cover: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Summary (max 500 chars)</label>
                <textarea rows={2} value={edit.summary||''} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50 resize-none" maxLength={500} />
              </div>
              {['challenge','approach','outcome'].map((f) => (
                <div key={f}>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">{f}</label>
                  <RichEditor value={edit[f]||''} onChange={(v) => setEdit({ ...edit, [f]: v })} placeholder={`Describe the ${f}...`} />
                </div>
              ))}
              <label className="flex items-center gap-3 text-cream-100/80">
                <input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-sage-500" /> Published
              </label>
            </div>
            <div className="p-5 border-t border-forest-800 flex justify-end gap-3 sticky bottom-0 bg-forest-900">
              <button onClick={() => setEdit(null)} className="px-4 py-2 border border-forest-700 text-cream-100/85 text-sm hover:border-sage-500">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-5 py-2 text-sm hover:bg-sage-600"><Save className="h-4 w-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
